/**
 * WorkOfferCard - Card de exibição de oferta de trabalho
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Exibição de detalhes da oferta
 * - Integração com chat para empregadores
 * - Ações de proprietário (desativar, excluir)
 * - Ver conversas sobre a oferta
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - useAuth (autenticação)
 * - axios (requisições API)
 * - Card components (shadcn/ui)
 * - MapPin, Clock (lucide-react)
 */

import React from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Clock } from "lucide-react";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import { Switch } from "./ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOfferCard = ({ offer, isOwner = false, onUpdate }) => {
  const { t } = useTranslation();
  // Get auth context for chat integration
  const { isAuthenticated, user } = useAuth();

  const handleStartChat = () => {
    // Dispatch custom event to open chat with professional
    const chatEvent = new CustomEvent('openChatWith', {
      detail: {
        partnerId: offer.professional_id,
        partnerName: offer.professional_name || 'Profissional',
        partnerType: 'professional',
        context: `Interesse na oferta: ${offer.title}`
      }
    });
    window.dispatchEvent(chatEvent);
  };

  const handleDeleteOffer = async () => {
    if (window.confirm("Tem certeza que deseja excluir esta oferta de trabalho? Esta ação não pode ser desfeita.")) {
      try {
        await axios.delete(`${API}/work-offers/${offer.id}`);
        if (onUpdate) onUpdate();
        alert("Oferta excluída com sucesso!");
      } catch (error) {
        alert("Erro ao excluir oferta: " + (error.response?.data?.detail || error.message));
      }
    }
  };

  const [toggling, setToggling] = React.useState(false);

  const handleToggleActive = async () => {
    const action = offer.is_active ? 'deactivate' : 'activate';
    setToggling(true);
    try {
      await axios.put(`${API}/work-offers/${offer.id}/${action}`);
      if (onUpdate) onUpdate();
    } catch (error) {
      alert("Erro ao alterar oferta: " + (error.response?.data?.detail || error.message));
    }
    setToggling(false);
  };

  return (
    <Card className={`hover:shadow-md transition-shadow ${!offer.is_active ? 'opacity-60 border-dashed' : ''}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-lg">{offer.title}</CardTitle>
              {offer.is_example && (
                <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                  Exemplo
                </Badge>
              )}
              {isOwner && !offer.is_active && (
                <Badge data-testid="offer-inactive-badge" className="bg-gray-100 text-gray-600 border border-gray-300 text-xs">
                  Inativa
                </Badge>
              )}
              {isOwner && offer.is_active && (
                <Badge data-testid="offer-active-badge" className="bg-green-100 text-green-700 border border-green-300 text-xs">
                  Ativa
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {offer.available_locations}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {offer.availability}
              </div>
            </div>
          </div>
          {offer.expected_salary && (
            <Badge variant="outline">{offer.expected_salary}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 mb-4">{offer.description}</p>
        <div className="space-y-2">
          <h4 className="font-medium">Habilidades:</h4>
          <p className="text-sm text-gray-600">{offer.skills}</p>
        </div>
        
        {/* CHAT INTEGRATION - Non-Owner Actions */}
        {!isOwner && isAuthenticated && user && user.user_type === 'employer' && (
          <div className="mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleStartChat}
              className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            >
              💬 Conversar com Profissional
            </Button>
          </div>
        )}
        
        {/* Owner Actions - Chat Integration for My Offers */}
        {isOwner && (
          <div className="mt-4 pt-4 border-t space-y-3">
            {/* Chat Indicator */}
            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">💬</span>
                <span className="text-sm text-blue-700 font-medium">
                  Conversas sobre esta oferta
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const chatEvent = new CustomEvent('openMyOfferChats', {
                    detail: {
                      offerId: offer.id,
                      offerTitle: offer.title
                    }
                  });
                  window.dispatchEvent(chatEvent);
                }}
                className="bg-white hover:bg-blue-100 text-blue-600 border-blue-200"
              >
                Ver Conversas
              </Button>
            </div>
            
            {/* Management Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="offer-toggle-active"
                  checked={offer.is_active}
                  onCheckedChange={handleToggleActive}
                  disabled={toggling}
                />
                <span className={`text-sm font-medium ${offer.is_active ? 'text-green-700' : 'text-gray-500'}`}>
                  {toggling ? 'Alterando...' : (offer.is_active ? 'Visível no quadro' : 'Oculta do quadro')}
                </span>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleDeleteOffer}
                data-testid="offer-delete-btn"
              >
                Excluir
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkOfferCard;
